import UnifiedLightboxModal, {
	LIGHTBOX_HASH,
} from '@components/unified-lightbox/Lightbox';
import {
	type VendorMediaKey,
	openUnifiedLightboxAtom,
} from '@components/unified-lightbox/atoms';
import type { UnifiedLightboxTrackingEvents } from '@components/unified-lightbox/hooks/use-track-unified-lightbox';
import { useStorefrontEmptyStates } from '@feature-flags/hooks/Storefront/useStorefrontEmptyStates';
import { useTrackStorefrontGalleryInteraction } from '@hooks/useTrackStorefrontGalleryInteraction';
import { useAppSelector } from '@redux/hooks';
import { useSetAtom } from 'jotai';
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import type { FCWithChildren } from 'types/react-extended';
import { afterNextPaint } from '../../../../../helpers/after-next-paint';
import Gallery from '../Gallery/Gallery';
import NavSection from '../nav/components/nav_section';
import Styles from './StorefrontGallery.scss';
import NewMobileGallery from './mobile_gallery/MobileGallery';

const PhotosSection: FCWithChildren = ({ children }) => (
	<NavSection navId="navPhotos" className={Styles.photoCarouselContainer}>
		{children}
	</NavSection>
);

const StorefrontGallery = () => {
	const onGalleryOpenTracking = useTrackStorefrontGalleryInteraction(
		'gallery',
		'Gallery Opened',
		{
			source: 'main_storefront',
		},
	);
	const onGalleryCloseTracking = useTrackStorefrontGalleryInteraction(
		'gallery',
		'Gallery Closed',
		{
			source: 'main_storefront',
		},
	);
	const onGalleryCategorySwitchedTracking =
		useTrackStorefrontGalleryInteraction(
			'gallery',
			'Gallery Category Switched',
			{
				source: 'main_storefront',
			},
		);
	const onGalleryMediaSwipedTracking = useTrackStorefrontGalleryInteraction(
		'gallery',
		'Gallery Media Swiped',
		{
			source: 'main_storefront',
		},
	);

	const configureGalleryTrackingEvents = {
		onGalleryOpenCallback:
			typeof onGalleryOpenTracking === 'function'
				? onGalleryOpenTracking
				: undefined,
		onGalleryCloseCallback:
			typeof onGalleryCloseTracking === 'function'
				? onGalleryCloseTracking
				: undefined,
		onGalleryCategorySwitchedCallback:
			typeof onGalleryCategorySwitchedTracking === 'function'
				? onGalleryCategorySwitchedTracking
				: undefined,
		onGalleryMediaSwipedCallback:
			typeof onGalleryMediaSwipedTracking === 'function'
				? onGalleryMediaSwipedTracking
				: undefined,
	} as UnifiedLightboxTrackingEvents;

	const isMobile = useAppSelector((state) => state.viewport.isMobile);
	const [isLightboxOpen, setLightboxOpen] = useState<boolean>(false);
	const [mediaId, setMediaIdForLightbox] = useState<string | undefined>(
		undefined,
	);

	const isEmptyStateEnabled = useStorefrontEmptyStates();

	const setOpenUnifiedLightbox = useSetAtom(openUnifiedLightboxAtom);
	const location = useLocation();

	const handleOpenUnifiedLightbox = async (
		indexInAllMedia: number,
		filter: VendorMediaKey,
	) => {
		afterNextPaint(() => {
			setOpenUnifiedLightbox({
				indexInAllMedia,
				filter,
				isEmptyStateEnabled,
				trackingEvents: configureGalleryTrackingEvents,
			});
			window.history.replaceState(null, '', `#${LIGHTBOX_HASH}`);
			setLightboxOpen(true);
		});
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: onMount
	useEffect(() => {
		// Check hash and query param after mount and open lightbox
		if (location.search && location.hash === `#${LIGHTBOX_HASH}`) {
			const params = new URLSearchParams(location.search);
			const mediaIdParam = params.get('mediaId');

			if (mediaIdParam) {
				setMediaIdForLightbox(mediaIdParam);
				handleOpenUnifiedLightbox(0, 'all');
			}
		}
	}, [location]);

	const onLightBoxClose = () => {
		setMediaIdForLightbox(undefined);
		setLightboxOpen(false);
	};

	if (isMobile) {
		return <NewMobileGallery trackingEvents={configureGalleryTrackingEvents} />;
	}

	return (
		<>
			<PhotosSection>
				<Gallery openUnifiedLightbox={handleOpenUnifiedLightbox} />
			</PhotosSection>
			{isLightboxOpen && (
				<UnifiedLightboxModal
					entryPoint="Mosaic"
					isModalOpen={isLightboxOpen}
					closeCallback={onLightBoxClose}
					mediaId={mediaId}
				/>
			)}
		</>
	);
};

export default StorefrontGallery;
